import { useState, FC, ReactNode } from 'react';
import ModalMount from '@type/ModalMount';
import { setCookie } from 'typescript-cookie';
import closeModal from '@functions/closeReactModal';
import Modal from '@components/Modal/Modal';
import Tile from '@components/persona-personalization/PersonaTile/PersonaTile';
import SavedAccount from '@icons/SavedAccount';
import Support from '@icons/Support';
import AppIntegration from '@icons/AppIntegration';
import Restaurant from '@icons/Restaurant';
import { useExitIntent } from 'use-exit-intent';

type Props = { initialOpen?: boolean } & ModalMount;

const PersonaPersonalization: FC<Props> = ({ mount, root, initialOpen = true }) => {
    const { registerHandler } = useExitIntent();

    registerHandler({
        id: 'openModal',
        handler: () => {
            if (!initialOpen) {
                setOpen(true);
            }
        },
    });

    const [open, setOpen] = useState(initialOpen);

    const handleClose = () => {
        setCookie('persona', 'none', {
            expires: 60,
            sameSite: 'lax',
            path: '/',
        });
        closeModal({ domElement: mount, appRoot: root, closeAction: setOpen });
    };

    const handlePersonaOne = () => {
        setCookie(`wpe-us`, `fasteasypersona`, {
            expires: 60,
            sameSite: 'lax',
            path: '/',
        });
        closeModal({ domElement: mount, appRoot: root, closeAction: setOpen });
    };
    const handlePersonaTwo = () => {
        setCookie('wpe-us', `supportseekerpersona`, {
            expires: 60,
            sameSite: 'lax',
            path: '/',
        });
        closeModal({ domElement: mount, appRoot: root, closeAction: setOpen });
    };
    const handlePersonaThree = () => {
        setCookie('wpe-us', `techsavvypersona`, {
            expires: 60,
            sameSite: 'lax',
            path: '/',
        });
        closeModal({ domElement: mount, appRoot: root, closeAction: setOpen });
    };
    const handlePersonaFour = () => {
        setCookie('wpe-us', `experiencedoperatorpersona`, {
            expires: 60,
            sameSite: 'lax',
            path: '/',
        });
        closeModal({ domElement: mount, appRoot: root, closeAction: setOpen });
    };
    const WrapperIcon: FC<{ children: ReactNode }> = ({ children }) => (
        <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-[80px] tw-bg-navy-600/0 [&_svg]:tw-h-[48px] [&_svg]:tw-w-[48px] [&_svg]:tw-fill-teal-400 [&_svg_path]:tw-fill-teal-400">
            {children}
        </div>
    );

    const SecondPageWrapperIcon: FC<{ children: ReactNode }> = ({ children }) => (
        <div className="tw-flex tw-h-12 tw-w-12 tw-p-1 [&_svg]:tw-h-full [&_svg]:tw-w-full [&_svg]:tw-fill-teal-400 [&_svg_path]:tw-fill-teal-400">
            {children}
        </div>
    );
    const SecondPageWrapperMultiIcon: FC<{ children: ReactNode }> = ({ children }) => (
        <div className="tw-flex">{children}</div>
    );

    return (
        <Modal
            isOpen={open}
            className={'!tw-bg-navy-800 lg:!tw-max-w-[750px]'}
            closeButtonClassName={'[&_svg]:!tw-fill-navy-600'}
            onClose={handleClose}
            appElement={mount}
        >
            <div id="content-1--personalization-modal" className="tw-p-6 tw-pt-0">
                <h2
                    className={
                        'tw-mb-6 tw-text-center !tw-font-graphik-medium !tw-text-[1.75rem] tw-leading-8 -tw-tracking-[1px] !tw-text-white xl:!tw-text-[2.5rem] xl:tw-leading-[3rem]'
                    }
                >
                    Hold on! Let us help improve your experience.
                </h2>
                <p className="tw-text-center tw-text-white">
                    Choose the description that fits you best and we’ll save you time by showing you exactly what you’re
                    looking for.
                </p>
                <div className={'-tw-mx-3 tw-flex tw-flex-wrap tw-justify-center tw-py-3'}>
                    <Tile
                        id={'personaOne'}
                        icon={
                            <WrapperIcon>
                                <SavedAccount />
                            </WrapperIcon>
                        }
                        handleClick={handlePersonaOne}
                        text={'I value ease of use, reliability, and a quick setup.'}
                    />
                    <Tile
                        id={'personaTwo'}
                        icon={
                            <WrapperIcon>
                                <Support />
                            </WrapperIcon>
                        }
                        handleClick={handlePersonaTwo}
                        text={'I value reliable customer support and a user-friendly experience.'}
                    />
                    <Tile
                        id={'personaThree'}
                        icon={
                            <WrapperIcon>
                                <AppIntegration />
                            </WrapperIcon>
                        }
                        handleClick={handlePersonaThree}
                        text={'I value sophisticated technology and ample access to integrations.'}
                    />
                    <Tile
                        id={'personaFour'}
                        icon={
                            <WrapperIcon>
                                <Restaurant />
                            </WrapperIcon>
                        }
                        handleClick={handlePersonaFour}
                        text={'I am an experienced restaurateur with specific features in mind.'}
                    />
                </div>
                <div className="tw-flex tw-w-full tw-justify-center">
                    <button className={'button button--as-link'} onClick={handleClose}>
                        No Thanks
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default PersonaPersonalization;
